import { Controller } from 'stimulus';
import Swiper, { A11y, Navigation, Pagination } from 'swiper';
import { AccessibilityHelper } from 'shared/assets/javascript/accessibility/accessibilityHelper.js';

// Register Swiper modules
Swiper.use([A11y, Navigation, Pagination]);

export default class extends Controller {
  static targets = ['slider', 'pagination', 'next', 'prev'];

  connect() {
    this.accessibilityHelper = new AccessibilityHelper;

    this.initSwiper();
  }

  disconnect() {
    this.destroySwiper();
  }

  /**
   * initSwiper
   * - Initialize the Swiper instance
   */
  initSwiper() {
    const slideLength = this.sliderTarget.querySelectorAll('.swiper-slide').length;
    if (slideLength <= 1) return;
    // Ensure Swiper is initialized only once
    if (this.swiper) return;

    this.swiper = new Swiper(this.sliderTarget, {
      slidesPerView: 1,
      loop: true,
      pagination: {
        el: this.paginationTarget,
        clickable: false,
        renderBullet: (index, className) => {
          return `
            <li class="swiper-pagination-item">
              <button class="${className}" type="button" data-index="${index}" ${index === 0 ? 'aria-current="true"' : ''}>
                <span class="sr-only">Go to slide ${index + 1}</span>
              </button>
            </li>
          `;
        },
      },
      navigation: {
        nextEl: this.nextTarget,
        prevEl: this.prevTarget,
      },
      speed: 450,
      a11y: {
        liveRegion: false,
        slideLabelMessage: 'Slide {{index}} of {{slidesLength}}',
      },
      on: {
        afterInit: (swiper) => {
          setTimeout(() => {
            swiper.wrapperEl.removeAttribute('aria-live');
            this.prevTarget.removeAttribute('aria-controls');
            this.nextTarget.removeAttribute('aria-controls');
          }, 300);

          this.addPaginationListeners(swiper);
        },

        slideChange: (swiper) => {
          this.updateAriaNotification();
        },
      },
    });

    this.swiper.on('slideChangeTransitionEnd', this.onSlideChangeTransitionEnd.bind(this));

    // Update the aria-notification element with the initial slide details
    this.updateAriaNotification();
    this.updateCarouselAccessibility();
  }

  onSlideChangeTransitionEnd(swiper) {
    this.updateCarouselAccessibility();
    this.updateBullets(swiper);
  }

  updateBullets(swiper) {
    if (!swiper || !swiper.pagination) return;

    const bullets = swiper.pagination.bullets;
    bullets.forEach((bullet, index) => {
      if (swiper.realIndex === index) {
        bullet.setAttribute('aria-current', 'true');
      } else {
        bullet.removeAttribute('aria-current');
      }
    });
  }

  addPaginationListeners(swiper) {
    const bullets = swiper.pagination.bullets;
    bullets.forEach((bullet) => this.addBulletListener(bullet, swiper));
  }

  addBulletListener(bullet, swiper) {
    bullet.addEventListener('click', () => swiper.slideToLoop(parseInt(bullet.dataset.index, 10)));
  }

  updateCarouselAccessibility() {
    const { updateElementAccessibility, isElementHorizontallyVisibleInParent } = this.accessibilityHelper;
    const slides = this.sliderTarget.querySelectorAll('.swiper-slide');
    slides.forEach(slide => {
      const options = { partialVisibility: false };
      if (isElementHorizontallyVisibleInParent(slide, this.sliderTarget, options)) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
        updateElementAccessibility(slide, true);
        return;
      }
      slide.setAttribute('aria-hidden', true);
      slide.setAttribute('inert', true);
      updateElementAccessibility(slide, false);
    });
  }

  /**
   * destroySwiper
   * - Destroy the Swiper instance, if it exists
   */
  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * updateAriaNotification
   * - Manually updates the swiper-notification element with slide change details
   */
  updateAriaNotification() {
    // Short-circuit if the Swiper instance is not initialized
    if (!this.swiper) return;

    // Find the swiper-notification element specific to this Swiper instance
    const notification = this.sliderTarget.querySelector('.swiper-notification');

    // Get the current slide index and total slides
    const currentSlideIndex = this.swiper?.realIndex + 1 ?? -1;
    const totalSlides = this.sliderTarget.dataset?.numSlides ?? this.swiper.slides.length;

    // If it exists and the currentSlideIndex is valid (Swiper is initialized properly)
    if (notification && currentSlideIndex > -1) {
      // Update the innerText of the notification element
      notification.innerText = `Slide ${currentSlideIndex} of ${totalSlides} is active.`;
    }
  }
}
